import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type {
  Evaluation,
  EvaluationQuestion,
  EvaluationOption,
  EvaluationAttempt,
} from '@lms/shared/types';

type ParticipantEvaluation = Omit<Evaluation, 'questions'> & {
  questions?: Array<
    Omit<EvaluationQuestion, 'options'> & {
      options?: Array<Omit<EvaluationOption, 'isCorrect'> & { isCorrect?: boolean }>;
    }
  >;
};

export const evaluationKeys = {
  all: ['evaluations'] as const,
  lists: () => [...evaluationKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown> | undefined) =>
    [...evaluationKeys.all, 'list', { filters }] as const,
  details: () => [...evaluationKeys.all, 'detail'] as const,
  detail: (id: string) => [...evaluationKeys.details(), id] as const,
};

export function useEvaluations(filters?: { programId?: string; userId?: string }) {
  return useQuery({
    queryKey: evaluationKeys.list(filters),
    queryFn: () => apiClient.evaluations.list(filters),
  });
}

export function useEvaluation(id: string, userId?: string, attemptId?: string) {
  return useQuery({
    queryKey: [...evaluationKeys.detail(id), userId, attemptId],
    queryFn: async (): Promise<ParticipantEvaluation> =>
      (await apiClient.evaluations.get(id, userId, attemptId)) as unknown as ParticipantEvaluation,
    enabled: !!id,
  });
}

export function useStartAttempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ evaluationId, userId }: { evaluationId: string; userId: string }) =>
      apiClient.evaluations.startAttempt(evaluationId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
    },
  });
}

export function useSubmitAttempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      evaluationId,
      userId,
      attemptId,
      answers,
    }: {
      evaluationId: string;
      userId: string;
      attemptId: string;
      answers: { questionId: string; optionId?: string; textAnswer?: string }[];
    }): Promise<EvaluationAttempt> => {
      try {
        return await apiClient.evaluations.submitAttempt(attemptId, answers);
      } catch (submitError) {
        // A lost HTTP response (or downstream progress error) does not mean
        // the database failed to save the submission. Read this exact attempt
        // before offering a retry; never submit a second answer set here.
        let savedAttempt: EvaluationAttempt | undefined;
        try {
          const review = await apiClient.evaluations.get(evaluationId, userId, attemptId);
          savedAttempt = review.attempts?.find(
            attempt => attempt.id === attemptId && attempt.submittedAt
          );
        } catch {
          // Completion could not be verified. Keep responses on the page.
        }
        if (savedAttempt) return savedAttempt;
        throw submitError;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
    },
  });
}
